import React, { useState, useEffect, useCallback } from "react";
import { useCreate, useDataProvider, useNotify, usePermissions } from "react-admin";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Divider,
  Autocomplete,
  CircularProgress,
  MenuItem,
  IconButton,
  Collapse,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import "moment/locale/es";
import { getEmployeeAvailableServices, checkPhoneExists } from "../components/dataProvider";
import countryCodes from "../Enterprise/Customer/phone_code.json";
import { useProvider } from "../ProviderContext";

// Métodos de pago como en Create.js
const paymentMethods = [
  { id: "Efectivo", name: "Efectivo" },
  { id: "Tarjeta", name: "Tarjeta" },
  { id: "QR", name: "QR" },
  { id: "Tigo Money", name: "Tigo Money" },
  { id: "Cortesia", name: "Cortesia" },
  { id: "Gift Card", name: "Gift Card" },
];

// Disponibilidad inicial por defecto
const initAvailability = {
  0: [],
  1: [],
  2: [],
  3: [],
  4: [],
  5: [],
  6: [],
};

// Componente para filtrar fechas disponibles
const AvailableDateInput = ({
  availability,
  blockedDates,
  busyDates,
  branchAvailability,
  setIsDateBlocked,
  fullbusyDates,
  selectedDate,
  setSelectedDate,
  ...props
}) => {
  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="es">
      <DatePicker
        label="Fecha de reserva"
        value={selectedDate}
        onChange={(newValue) => setSelectedDate(newValue)}
        renderInput={(params) => <TextField {...params} fullWidth />}
        minDate={moment()}
        shouldDisableDate={(day) => {
          const dateMoment = moment(day);

          // No deshabilitar fechas pasadas ya que minDate se encarga de eso
          if (dateMoment.isBefore(moment().startOf("day"))) {
            return true;
          }

          // Verificar si está en fullbusyDates
          if (fullbusyDates && fullbusyDates.includes(dateMoment.format("YYYY-MM-DD"))) {
            return true;
          }

          // Verificar fechas bloqueadas
          if (blockedDates && blockedDates.some((blockedDate) => dateMoment.isSame(blockedDate, "day"))) {
            return true;
          }

          // Verificar disponibilidad por día de la semana
          if (availability) {
            const dayIndex = dateMoment.isoWeekday() - 1; // 0 = lunes, 6 = domingo
            if (!availability[dayIndex] || availability[dayIndex].length === 0) {
              return true;
            }
          }

          return false;
        }}
        {...props}
      />
    </LocalizationProvider>
  );
};

// Componente para filtrar horas disponibles
const HourInput = ({
  _availability,
  _branchAvailability,
  isDateBlocked,
  selectedDate,
  selectedHour,
  setSelectedHour,
  selectedEmployee,
  selectedServices,
  totalEstimatedWorkMinutes,
  initialHour, // Hora original del bloque seleccionado
}) => {
  const [hourPicker, setHourPicker] = useState([]);
  const [create] = useCreate();
  const [showTimeMessage, setShowTimeMessage] = useState(false);
  const [unavailableHour, setUnavailableHour] = useState("");

  const _setHourPicker = useCallback(
    async (date) => {
      try {
        // Si no hay servicios seleccionados, solo mostrar la hora inicial
        if (!selectedServices || selectedServices.length === 0) {
          if (initialHour) {
            const initialHourOption = {
              id: initialHour,
              name: initialHour,
            };
            setHourPicker([initialHourOption]);
            // Auto-seleccionar la hora inicial si no está ya seleccionada
            if (selectedHour !== initialHour) {
              setSelectedHour(initialHour);
            }
          } else {
            setHourPicker([]);
          }
          return;
        }

        // Si hay servicios seleccionados, llamar al endpoint
        const profile = JSON.parse(localStorage.getItem("profile"));
        const providerId = profile?.role === "admin-sucursal" ? profile?.employer?._id : profile?._id;

        await create(
          "dateAvailability/" + providerId,
          {
            data: {
              date: date,
              estimatedTime: totalEstimatedWorkMinutes || 0,
              isInBranch: true,
              branch: selectedEmployee?.branch?._id,
              employee: selectedEmployee?._id,
              serviceCart: selectedServices?.map((e) => e._id) ?? [],
            },
          },
          {
            onSuccess: (response) => {
              const newHourPicker = [];
              const availableHours = response.data.availability || [];

              availableHours.forEach((timeString) => {
                // Parsear la hora del string ISO o formato HH:mm
                let time;
                if (timeString.includes("T")) {
                  // Formato ISO: "2025-10-15T08:00:00-04:00"
                  time = moment(timeString);
                } else {
                  // Formato HH:mm: "08:00"
                  time = moment(timeString, "HH:mm");
                }

                newHourPicker.push({
                  id: time.format("HH:mm"),
                  name: time.format("HH:mm"),
                });
              });

              setHourPicker(newHourPicker);

              // Auto-rellenar con la hora inicial si está disponible
              if (initialHour && newHourPicker.some((h) => h.id === initialHour)) {
                setSelectedHour(initialHour);
                setShowTimeMessage(false);
                setUnavailableHour("");
              } else if (newHourPicker.length > 0 && !newHourPicker.some((h) => h.id === selectedHour)) {
                // Si la hora actual no está disponible, seleccionar la primera disponible
                const originalHour = selectedHour || initialHour;
                setSelectedHour(newHourPicker[0].id);
                // Mostrar mensaje solo si había una hora inicial seleccionada
                if (originalHour) {
                  setUnavailableHour(originalHour);
                  setShowTimeMessage(true);
                }
              }
            },
            onError: () => {
              setHourPicker([]);
            },
          }
        );
      } catch (error) {
        setHourPicker([]);
      }
    },
    [create, totalEstimatedWorkMinutes, selectedEmployee, selectedServices, initialHour, selectedHour, setSelectedHour]
  );

  useEffect(() => {
    if (selectedDate && selectedEmployee) {
      _setHourPicker(selectedDate);
    }
  }, [selectedDate, totalEstimatedWorkMinutes, selectedEmployee, selectedServices, _setHourPicker]);

  return (
    <Box>
      <TextField
        select
        label="Hora"
        value={selectedHour}
        onChange={(e) => {
          setSelectedHour(e.target.value);
          // Ocultar mensaje cuando el usuario cambie manualmente la hora
          if (showTimeMessage) {
            setShowTimeMessage(false);
            setUnavailableHour("");
          }
        }}
        fullWidth
        disabled={hourPicker.length === 0 || isDateBlocked}
        helperText={
          isDateBlocked
            ? "Sin horarios disponibles en esa fecha."
            : hourPicker.length === 0
              ? "Seleccione un empleado y fecha válidos."
              : selectedServices.length === 0
                ? ""
                : ""
        }
      >
        {hourPicker.map((hour) => (
          <MenuItem key={hour.id} value={hour.id}>
            {hour.name}
          </MenuItem>
        ))}
      </TextField>
      {showTimeMessage && unavailableHour && (
        <Alert
          severity="info"
          sx={{
            mt: 1,
            fontSize: "0.875rem",
            "& .MuiAlert-message": { fontSize: "0.875rem" },
          }}
        >
          Su hora {unavailableHour} no se encuentra disponible. Revise el tiempo estimado del servicio.
        </Alert>
      )}
    </Box>
  );
};

const CreateCustomerForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    countryCode: countryCodes.paises.find((e) => e.code === "BO")?.dial_code || "591",
    birthdate: "",
    gender: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.fullName.trim() || !formData.phone.trim()) {
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData);
      setFormData({
        fullName: "",
        phone: "",
        email: "",
        countryCode: countryCodes.paises.find((e) => e.code === "BO")?.dial_code || "591",
        birthdate: "",
        gender: "",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 2, p: 2, border: 1, borderColor: "grey.300", borderRadius: 1, backgroundColor: "grey.50" }}>
      <Typography variant="h6" gutterBottom>
        Crear Nuevo Cliente
      </Typography>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Nombre Completo"
              value={formData.fullName}
              onChange={handleChange("fullName")}
              required
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              select
              label="Código de País"
              value={formData.countryCode}
              onChange={handleChange("countryCode")}
              size="small"
              fullWidth
            >
              {countryCodes.paises.map((country) => (
                <MenuItem key={country.dial_code} value={country.dial_code}>
                  {country.name} (+{country.dial_code})
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={8}>
            <TextField
              label="Teléfono"
              value={formData.phone}
              onChange={handleChange("phone")}
              required
              fullWidth
              size="small"
              type="tel"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Email (opcional)"
              value={formData.email}
              onChange={handleChange("email")}
              type="email"
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Fecha de Nacimiento"
              value={formData.birthdate}
              onChange={handleChange("birthdate")}
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              select
              label="Género"
              value={formData.gender}
              onChange={handleChange("gender")}
              fullWidth
              size="small"
            >
              <MenuItem value="">Seleccionar género</MenuItem>
              <MenuItem value="hombre">Hombre</MenuItem>
              <MenuItem value="mujer">Mujer</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button onClick={onCancel} disabled={loading} size="small">
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading || !formData.fullName.trim() || !formData.phone.trim()}
                size="small"
              >
                {loading ? <CircularProgress size={20} /> : "Crear Cliente"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

const CustomerAutocomplete = ({ selectedCustomer, setSelectedCustomer }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingCustomerData, setPendingCustomerData] = useState(null);
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);
  const [create] = useCreate();
  const notify = useNotify();

  // Obtener providerId como en SelectCustomer.js
  const profile = JSON.parse(localStorage.getItem("profile"));
  const providerId = profile?.role === "admin-sucursal" ? profile?.employer?._id : profile?._id;

  const fetchCustomers = useCallback(
    async (searchText = "") => {
      setLoading(true);
      try {
        const filter = {
          $or: [{ fullName: { $regex: searchText, $options: "i" } }, { phone: { $regex: searchText, $options: "i" } }],
        };

        if (providerId) {
          filter.providerId = providerId;
        }

        const url = `${process.env.REACT_APP_API_URL || "/api"}/enterprise/customers?filter=${encodeURIComponent(JSON.stringify(filter))}&page=1&perPage=25&sort=-id`;

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          setCustomers(data.data || []);
        } else {
          notify("Error al cargar clientes", { type: "warning" });
        }
      } catch (error) {
        notify("Error de conexión", { type: "error" });
      } finally {
        setLoading(false);
      }
    },
    [providerId, notify]
  );

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
    if (newInputValue.length >= 2 || newInputValue.length === 0) {
      fetchCustomers(newInputValue);
    }
  };

  const handleCustomerChange = (event, newValue) => {
    setSelectedCustomer(newValue);
  };

  const handleCreateCustomer = async (customerData) => {
    // Si no hay teléfono o es "0", crear directamente
    if (!customerData.phone || customerData.phone === "0") {
      return createCustomerDirect(customerData);
    }

    setIsCheckingPhone(true);
    try {
      const result = await checkPhoneExists(customerData.phone);

      if (result.exists) {
        // Mostrar modal de confirmación
        setPendingCustomerData(customerData);
        setShowConfirmModal(true);
      } else {
        // Crear cliente directamente
        createCustomerDirect(customerData);
      }
    } catch (error) {
      notify("Error al verificar el teléfono", { type: "error" });
    } finally {
      setIsCheckingPhone(false);
    }
  };

  const createCustomerDirect = async (customerData) => {
    try {
      // Crear objeto solo con campos que tienen datos
      const newCustomer = {
        fullName: customerData.fullName,
        phone: customerData.phone,
        countryCode: customerData.countryCode,
        providerId: providerId,
      };

      // Solo agregar campos opcionales si tienen valor
      if (customerData.email && customerData.email.trim()) {
        newCustomer.email = customerData.email.trim();
      }

      if (customerData.birthdate && customerData.birthdate.trim()) {
        newCustomer.birthdate = customerData.birthdate.trim();
      }

      if (customerData.gender && customerData.gender.trim()) {
        newCustomer.gender = customerData.gender.trim();
      }

      create(
        "enterprise/customers",
        { data: newCustomer },
        {
          onSuccess: (result) => {
            notify("Cliente creado exitosamente", { type: "success" });
            setShowCreateForm(false);
            // Autoseleccionar el cliente creado
            setSelectedCustomer(result);
            // Recargar la lista de clientes
            fetchCustomers();
          },
          onError: (error) => {
            notify("Error al crear cliente", { type: "error" });
          },
        }
      );
    } catch (error) {
      notify("Error al procesar los datos del cliente", { type: "error" });
    }
  };

  const handleConfirmCreate = () => {
    setShowConfirmModal(false);
    createCustomerDirect(pendingCustomerData);
    setPendingCustomerData(null);
  };

  const handleCancelCreate = () => {
    setShowConfirmModal(false);
    setPendingCustomerData(null);
  };

  return (
    <Box>
      <Autocomplete
        value={selectedCustomer}
        onChange={handleCustomerChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        options={customers}
        getOptionLabel={(option) => {
          if (typeof option === "string") return option;
          return `${option.fullName || ""} - ${option.phone || ""}`.trim();
        }}
        loading={loading}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Buscar cliente"
            placeholder="Escriba el nombre o teléfono del cliente"
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Box>
              <Typography variant="body1">{option.fullName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {option.phone} {option.email && `• ${option.email}`}
              </Typography>
            </Box>
          </Box>
        )}
        noOptionsText={inputValue.length < 2 ? "Escriba al menos 2 caracteres" : "No se encontraron clientes"}
      />

      <Button
        variant="outlined"
        startIcon={showCreateForm ? <ExpandLessIcon /> : <AddIcon />}
        onClick={() => setShowCreateForm(!showCreateForm)}
        sx={{
          mt: 1,
          color: "secondary.main",
          borderColor: "secondary.main",
          "&:hover": {
            borderColor: "secondary.main",
            backgroundColor: "rgba(255, 119, 13, 0.1)", // el mismo naranja pero muy suave
          },
        }}
      >
        {showCreateForm ? "Cancelar" : "Crear Nuevo Cliente"}
      </Button>

      <Collapse in={showCreateForm}>
        <CreateCustomerForm onSubmit={handleCreateCustomer} onCancel={() => setShowCreateForm(false)} />
      </Collapse>

      {/* Modal de confirmación para teléfono duplicado */}
      <Dialog open={showConfirmModal} onClose={handleCancelCreate} maxWidth="sm" fullWidth>
        <DialogTitle>Teléfono ya registrado</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Ya existe un cliente registrado con el número de teléfono <strong>{pendingCustomerData?.phone}</strong>.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            ¿Deseas continuar y crear el cliente de todas formas?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCreate} color="secondary" variant="outlined">
            No, cancelar
          </Button>
          <Button onClick={handleConfirmCreate} color="primary" variant="contained" disabled={isCheckingPhone}>
            Sí, continuar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const QuickBookingModal = ({ open, onClose, onSuccess, schedulerData }) => {
  const [availableServices, setAvailableServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedDate, setSelectedDate] = useState(schedulerData?.date ? moment(schedulerData.date) : moment());
  const [selectedHour, setSelectedHour] = useState(moment(schedulerData?.date).format("HH:mm"));
  const [initialHour, setInitialHour] = useState(moment(schedulerData?.date).format("HH:mm")); // Hora original del bloque
  const [selectedEmployee, setSelectedEmployee] = useState(null); // Siempre será un objeto completo o null
  const [selectedBranch, setSelectedBranch] = useState(schedulerData?.branch || null);
  const [_selectedServiceMethod, _setSelectedServiceMethod] = useState(schedulerData?.serviceMethod || "Ambos");
  const [loading, setLoading] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [_loadingEmployees, setLoadingEmployees] = useState(false);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [availableBranches, setAvailableBranches] = useState([]);

  // Estados para información adicional
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("Efectivo");
  const [whatsappSettings, setWhatsappSettings] = useState({
    sendOnCreate: true,
    sendOnFinish: true,
    sendQRCode: true,
    sendOnCancel: true,
  });

  // Estados para filtrado de fechas y horas (como en Create.js)
  const [availability, setAvailability] = useState(initAvailability);
  const [blockedDates, setBlockedDates] = useState([]);
  const [busyDates, setBusyDates] = useState([]);
  const [fullbusyDates, setFullBusyDates] = useState([]);
  const [branchAvailability, _setBranchAvailability] = useState(null);
  const [isDateBlocked, setIsDateBlocked] = useState(false);
  const [totalEstimatedWorkMinutes, setTotalEstimatedWorkMinutes] = useState(0);

  const notify = useNotify();
  const [create] = useCreate();
  const dataProvider = useDataProvider();
  const { permissions } = usePermissions();
  const { provider } = useProvider();

  // Configurar moment en español
  moment.locale("es");

  // Función para obtener fechas bloqueadas (como en Create.js)
  const getBlockedDates = (unavailablePeriods) => {
    const blockedDates = [];

    unavailablePeriods.forEach(({ startDate, endDate }) => {
      const start = moment(startDate);
      const end = moment(endDate);

      const startDay = start.clone().startOf("day");
      const endDay = end.clone().startOf("day");

      while (startDay.isSameOrBefore(endDay)) {
        if (startDay.isSame(start, "day")) {
          // Primer día - verificar si todo el día está bloqueado
          const dayStart = start.clone().startOf("day");
          const dayEnd = start.clone().endOf("day");
          if (start.isSameOrBefore(dayStart) && end.isSameOrAfter(dayEnd)) {
            blockedDates.push(startDay.clone());
          }
        } else if (startDay.isSame(end, "day")) {
          // Último día - verificar si todo el día está bloqueado
          const dayStart = end.clone().startOf("day");
          const dayEnd = end.clone().endOf("day");
          if (start.isSameOrBefore(dayStart) && end.isSameOrAfter(dayEnd)) {
            blockedDates.push(startDay.clone());
          }
        } else {
          // Días intermedios - completamente bloqueados
          blockedDates.push(startDay.clone());
        }

        startDay.add(1, "day");
      }
    });

    return blockedDates;
  };

  // Obtener datos de disponibilidad del empleado usando el endpoint de availability
  const fetchEmployeeAvailability = useCallback(async (employeeId) => {
    if (!employeeId) return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_API_URL}/availability/${employeeId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const userData = data.data;
      // console.log("UserDATA!!", userData);
      if (userData) {
        // Establecer datos de disponibilidad como en Create.js
        if (userData.busy) {
          setBusyDates(userData.busy);
        }
        if (userData.fullyBusyDays) {
          setFullBusyDates(userData.fullyBusyDays);
        }
        if (userData.unavailability) {
          setBlockedDates(getBlockedDates(userData.unavailability));
        }
        if (userData.availability) {
          const newAvailability = { ...initAvailability };
          userData.availability.forEach((dayAvailability) => {
            // Usar dayIndex del objeto para colocarlo en el índice correcto
            const dayIndex = dayAvailability.dayIndex;
            if (dayIndex !== undefined && dayIndex >= 0 && dayIndex <= 6) {
              // Crear array como en Create.js
              newAvailability[dayIndex] = [dayAvailability];
            }
          });
          setAvailability(newAvailability);
        }
      }
    } catch (error) {
      // Error silencioso, mantener estados por defecto
    }
  }, []);

  // Calcular tiempo total estimado de servicios seleccionados
  useEffect(() => {
    if (selectedServices && selectedServices.length > 0) {
      const total = selectedServices.reduce((acc, service) => {
        return acc + (service.unitEstimatedWorkMinutes || 0);
      }, 0);
      setTotalEstimatedWorkMinutes(total);
    } else {
      setTotalEstimatedWorkMinutes(0);
    }
  }, [selectedServices]);

  // Variables para facilitar el acceso
  const branches = availableBranches;
  const employees = availableEmployees;

  // Cargar sucursales disponibles
  const loadBranches = useCallback(async () => {
    try {
      const response = await dataProvider.getList("enterprise/branches", {
        pagination: { page: 1, perPage: 50 },
        sort: { field: "name", order: "ASC" },
        filter: {},
      });
      setAvailableBranches(response.data || []);
    } catch (error) {
      notify("Error al cargar sucursales", { type: "error" });
    }
  }, [dataProvider, notify]);

  // Cargar empleados disponibles según la sucursal
  const loadEmployees = useCallback(
    async (branchId) => {
      if (!branchId) return;

      setLoadingEmployees(true);
      try {
        const profile = JSON.parse(localStorage.getItem("profile"));
        const providerId = profile?.role === "admin-sucursal" ? profile?.employer?._id : profile?._id;

        const response = await dataProvider.getList(`employees/${providerId}`, {
          pagination: { page: 1, perPage: 50 },
          sort: { field: "first_name", order: "ASC" },
          filter: { branchId: branchId },
        });
        setAvailableEmployees(response.data || []);
      } catch (error) {
        notify("Error al cargar empleados", { type: "error" });
      } finally {
        setLoadingEmployees(false);
      }
    },
    [dataProvider, notify]
  );

  // Cargar servicios disponibles del empleado
  const loadEmployeeServices = useCallback(
    async (employeeId) => {
      if (!employeeId) return;

      setLoadingServices(true);
      try {
        const data = await getEmployeeAvailableServices(employeeId);
        if (data?.services) {
          setAvailableServices(data.services);
        }
      } catch (error) {
        notify("Error al cargar servicios", { type: "error" });
      } finally {
        setLoadingServices(false);
      }
    },
    [notify]
  );

  // Cargar empleado por ID para autocompletado inicial
  const loadEmployeeById = useCallback(async (employeeId) => {
    if (!employeeId) return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_API_URL}/profile/${employeeId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.data) {
        setSelectedEmployee(data.data); // Establecer el objeto completo
        // Auto-seleccionar la sucursal del empleado si tiene una
        if (data.data.branch) {
          setSelectedBranch(data.data.branch);
        }
        // Los servicios se cargarán automáticamente por el useEffect de selectedEmployee
      }
    } catch (error) {
      // Error al cargar empleado
    }
  }, []);

  // Efectos de inicialización
  useEffect(() => {
    if (open) {
      loadBranches();
      // Cargar empleado inicial si viene en schedulerData
      if (schedulerData?.employeeId) {
        loadEmployeeById(schedulerData.employeeId);
      }
    }
  }, [open, loadBranches, loadEmployeeById, schedulerData?.employeeId]);

  useEffect(() => {
    if (selectedBranch?._id) {
      loadEmployees(selectedBranch._id);
    }
  }, [selectedBranch, loadEmployees]);

  useEffect(() => {
    if (selectedEmployee && selectedEmployee._id) {
      // Solo llamar a los servicios cuando tenemos un empleado completo con _id
      loadEmployeeServices(selectedEmployee._id);
      // Cargar disponibilidad del empleado
      fetchEmployeeAvailability(selectedEmployee._id);
    }
  }, [selectedEmployee, loadEmployeeServices, fetchEmployeeAvailability]);

  // Actualizar datos cuando cambian los schedulerData
  useEffect(() => {
    if (schedulerData) {
      setSelectedDate(schedulerData.date ? moment(schedulerData.date) : moment());
      const hourFromScheduler = moment(schedulerData.date).format("HH:mm");
      setSelectedHour(hourFromScheduler);
      setInitialHour(hourFromScheduler); // Guardar la hora inicial del bloque
      setSelectedBranch(schedulerData.branch || null);
      // setSelectedServiceMethod(schedulerData.serviceMethod || "Ambos");
      // No establecemos selectedEmployee aquí porque se carga en loadEmployeeById
    }
  }, [schedulerData]);

  const handleRemoveService = (serviceToRemove) => {
    setSelectedServices((prev) => prev.filter((service) => service._id !== serviceToRemove._id));
  };

  // Función transform para estructurar los datos como en Create.js
  const transform = async () => {
    const profile = JSON.parse(localStorage.getItem("profile"));
    const messageToSend = profile?.message || "";
    let providerData = provider || profile; // Usar provider del contexto primero

    if (permissions === "admin-sucursal") {
      providerData = profile.employer;
      try {
        const response = await dataProvider.getOne("providers", { id: profile.employer._id });
        providerData = response.data;
      } catch (error) {
        // console.error("Error getting provider data:", error);
      }
    }

    // Calcular precio total
    const totalPrice = selectedServices.reduce((total, service) => total + (service.unitPrice || 0), 0);

    // Combinar fecha y hora
    const [hours, minutes] = selectedHour.split(":");
    const combinedDateTime = moment(selectedDate).hours(parseInt(hours)).minutes(parseInt(minutes)).seconds(0);

    const booking = {
      isInBranch: true,
      serviceCart: selectedServices.map((service) => ({
        service: service,
        quantity: 1,
        price: service.unitPrice,
        estimatedWorkMinutes: service.unitEstimatedWorkMinutes,
      })),
      discountAmount: null,
      discountPercentage: null,
      editedEstimatedWorkMinutes: totalEstimatedWorkMinutes,
      isPriceVariable: false,
      paymentInfo: {
        totalPrice: totalPrice,
        paymentMethod: paymentMethod,
        sobreHorario: false,
      },
      employee: {
        ...selectedEmployee,
        fullName: selectedEmployee.first_name + " " + selectedEmployee.last_name,
        photoURL: selectedEmployee.picture,
        CI: selectedEmployee.CI_NIT,
        pushToken: selectedEmployee.pushToken,
      },
      date: combinedDateTime.toISOString(),
      hour: parseInt(hours, 10) + parseInt(minutes, 10) / 60, // Convertir a formato decimal como en Create.js
      createdFrom: "Manual",
      whatsappSettings: {
        sendOnCreate: whatsappSettings.sendOnCreate,
        sendOnFinish: whatsappSettings.sendOnFinish,
        sendQRCode: whatsappSettings.sendQRCode,
        sendOnCancel: whatsappSettings.sendOnCancel,
      },
      branch: selectedBranch,
      customer: selectedCustomer,
      totalEstimatedWorkMinutes: totalEstimatedWorkMinutes,
      bookingDate: combinedDateTime.format(),
      createdAt: moment().format(),
      status: "Confirmado",
      provider: {
        ...providerData,
        name: providerData.first_name,
        logoURL: providerData.picture,
      },
    };

    // Agregar reminder si el cliente tiene habilitado recordatorios
    if (selectedCustomer?.reminderEnabled && messageToSend) {
      booking.reminder = {
        isActive: true,
        message: messageToSend,
        isDone: false,
        totalToSend: selectedServices.length,
        alreadySent: 0,
        datesInfo: [],
      };
    }

    return booking;
  };

  const handleSubmit = async () => {
    if (!selectedCustomer || selectedServices.length === 0 || !selectedEmployee || !selectedBranch) {
      notify("Debe seleccionar un cliente, empleado, sucursal y al menos un servicio", { type: "error" });
      return;
    }

    // Validación adicional para asegurar que selectedBranch tenga un _id válido
    if (!selectedBranch._id) {
      notify("La sucursal seleccionada no es válida. Por favor, seleccione una sucursal válida.", { type: "error" });
      return;
    }

    setLoading(true);
    try {
      const bookingData = await transform();

      // Usar create con callbacks en lugar de async/await
      create(
        "booking",
        { data: bookingData },
        {
          onSuccess: () => {
            notify("Reserva creada exitosamente", { type: "success" });
            setLoading(false);

            // Si tenemos onSuccess, usarla para cerrar ambos modales y refrescar
            if (onSuccess) {
              onSuccess();
            } else {
              // Fallback al comportamiento anterior
              onClose();
            }
          },
          onError: () => {
            // console.error("Error creating booking:", error);
            notify("Error al crear la reserva", { type: "error" });
            setLoading(false);
          },
        }
      );
    } catch (error) {
      // Error en transform
      // console.error("Error transforming data:", error);
      notify("Error al procesar los datos", { type: "error" });
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedServices([]);
    setSelectedCustomer(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: "70vh" },
      }}
    >
      <DialogTitle>
        <Typography variant="h6">Reserva Rápida</Typography>
        <Typography variant="body2" color="text.secondary">
          {moment(schedulerData?.date).format("dddd, DD [de] MMMM [de] YYYY [a las] HH:mm")}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ padding: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Información editable de la Reserva */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Información de la Reserva
            </Typography>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <Autocomplete
                options={branches}
                getOptionLabel={(option) => option.name || ""}
                value={selectedBranch}
                onChange={(event, newValue) => setSelectedBranch(newValue)}
                isOptionEqualToValue={(option, value) => option._id === value?._id}
                renderInput={(params) => <TextField {...params} label="Sucursal" fullWidth />}
                disabled
              />
              <Autocomplete
                options={employees}
                getOptionLabel={(option) => option.first_name + " " + option.last_name || ""}
                value={selectedEmployee}
                onChange={(event, newValue) => {
                  setSelectedEmployee(newValue);
                  if (newValue) {
                    loadEmployeeServices(newValue._id);
                    // Auto-seleccionar la sucursal del empleado si tiene una
                    if (newValue.branch) {
                      setSelectedBranch(newValue.branch);
                    }
                  }
                }}
                isOptionEqualToValue={(option, value) => option._id === value?._id}
                renderInput={(params) => <TextField {...params} label="Empleado" fullWidth />}
                disabled
              />
            </Box>
            {loadingServices ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ mt: 2 }}>
                <Autocomplete
                  multiple
                  options={availableServices}
                  getOptionLabel={(option) =>
                    `${option.name} - Bs. ${option.unitPrice} (${option.unitEstimatedWorkMinutes} min)`
                  }
                  value={selectedServices}
                  onChange={(event, newValue) => {
                    setSelectedServices(newValue);
                  }}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Seleccionar servicios"
                      placeholder="Buscar y seleccionar servicios..."
                      fullWidth
                    />
                  )}
                  renderTags={() => null}
                  ListboxProps={{
                    style: {
                      maxHeight: 200,
                    },
                  }}
                  PopperProps={{
                    placement: "bottom-start",
                    modifiers: [
                      {
                        name: "flip",
                        enabled: false,
                      },
                    ],
                  }}
                  renderOption={(props, option, { selected }) => (
                    <Box component="li" {...props}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body1" fontWeight={selected ? "bold" : "normal"}>
                          {option.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.description}
                        </Typography>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            Bs. {option.unitPrice}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {option.unitEstimatedWorkMinutes} minutos
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                  noOptionsText="No hay servicios disponibles"
                />

                {selectedServices.length > 0 && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      Resumen de servicios seleccionados:
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {selectedServices.map((service) => (
                        <Box
                          key={service._id}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            p: 1,
                            bgcolor: "white",
                            borderRadius: 0.5,
                            border: 1,
                            borderColor: "grey.200",
                          }}
                        >
                          <Typography variant="body2">{service.name}</Typography>
                          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                            <Typography variant="body2" fontWeight="bold">
                              Bs. {service.unitPrice}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {service.unitEstimatedWorkMinutes} min
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveService(service)}
                              sx={{ ml: 1, color: "error.main" }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                      <Divider />
                      <Box sx={{ display: "flex", justifyContent: "space-between", fontWeight: "bold" }}>
                        <Typography variant="body2">Total: {selectedServices.length} servicio(s)</Typography>
                        <Typography variant="body2" color="primary">
                          Bs. {selectedServices.reduce((total, service) => total + service.unitPrice, 0)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          <Divider />

          {/* Selección de Cliente */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Cliente *
            </Typography>
            <CustomerAutocomplete selectedCustomer={selectedCustomer} setSelectedCustomer={setSelectedCustomer} />
          </Box>

          <Divider />

          {/* Selección de Servicios */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Fecha y hora *
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 2 }}>
              <AvailableDateInput
                availability={availability}
                blockedDates={blockedDates}
                busyDates={busyDates}
                branchAvailability={branchAvailability}
                setIsDateBlocked={setIsDateBlocked}
                fullbusyDates={fullbusyDates}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
              />
              <HourInput
                availability={availability}
                branchAvailability={branchAvailability}
                isDateBlocked={isDateBlocked}
                selectedDate={selectedDate}
                selectedHour={selectedHour}
                setSelectedHour={setSelectedHour}
                selectedEmployee={selectedEmployee}
                selectedServices={selectedServices}
                totalEstimatedWorkMinutes={totalEstimatedWorkMinutes}
                initialHour={initialHour}
              />
            </Box>
          </Box>

          <Divider />

          {/* Información Adicional */}
          <Box>
            <Button
              variant="outlined"
              startIcon={showAdditionalInfo ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setShowAdditionalInfo(!showAdditionalInfo)}
              sx={{
                justifyContent: "flex-start",
                textTransform: "none",
                color: "text.primary",
                borderColor: "grey.300",
                "&:hover": {
                  borderColor: "grey.400",
                  backgroundColor: "grey.50",
                },
                py: 1.5,
              }}
              fullWidth
            >
              Información Adicional
            </Button>

            <Collapse in={showAdditionalInfo}>
              <Box
                sx={{ mt: 2, p: 2, border: 1, borderColor: "grey.300", borderRadius: 1, backgroundColor: "grey.50" }}
              >
                <Grid container spacing={2}>
                  {/* Método de Pago */}
                  <Grid item xs={12}>
                    <TextField
                      select
                      label="Método de Pago"
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      fullWidth
                      size="small"
                    >
                      {paymentMethods.map((method) => (
                        <MenuItem key={method.id} value={method.id}>
                          {method.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Configuración de WhatsApp */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                      Configuración de Mensajes WhatsApp
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={whatsappSettings.sendOnCreate}
                            onChange={(e) =>
                              setWhatsappSettings((prev) => ({
                                ...prev,
                                sendOnCreate: e.target.checked,
                              }))
                            }
                            size="small"
                          />
                        }
                        label="Enviar WhatsApp al crear reserva"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={whatsappSettings.sendOnFinish}
                            onChange={(e) =>
                              setWhatsappSettings((prev) => ({
                                ...prev,
                                sendOnFinish: e.target.checked,
                              }))
                            }
                            size="small"
                          />
                        }
                        label="Enviar WhatsApp al finalizar servicio"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={whatsappSettings.sendQRCode}
                            onChange={(e) =>
                              setWhatsappSettings((prev) => ({
                                ...prev,
                                sendQRCode: e.target.checked,
                              }))
                            }
                            size="small"
                          />
                        }
                        label="Enviar código QR por WhatsApp"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={whatsappSettings.sendOnCancel}
                            onChange={(e) =>
                              setWhatsappSettings((prev) => ({
                                ...prev,
                                sendOnCancel: e.target.checked,
                              }))
                            }
                            size="small"
                          />
                        }
                        label="Enviar WhatsApp al cancelar reserva"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: 3, paddingTop: 0 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            loading ||
            !selectedCustomer ||
            selectedServices.length === 0 ||
            !selectedEmployee ||
            !selectedBranch ||
            !selectedBranch?._id
          }
        >
          {loading ? <CircularProgress size={20} /> : "Crear Reserva"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickBookingModal;
